import { Controller } from "@hotwired/stimulus"
import { createIcons, icons } from "lucide"

export default class extends Controller {
  static targets = ["panel", "backdrop", "shell", "label", "childrenGroup", "navLink", "toggleIcon", "title", "childrenToggleIcon"]

  connect() {
    this.mediaQuery = window.matchMedia("(min-width: 1024px)")
    this.handleViewportChange = this.handleViewportChange.bind(this)

    if (this.mediaQuery.addEventListener) {
      this.mediaQuery.addEventListener("change", this.handleViewportChange)
    } else {
      this.mediaQuery.addListener(this.handleViewportChange)
    }

    this.desktopCollapsed = window.localStorage.getItem("sidebar:desktop-collapsed") === "1"
    this.mobileOpen = false
    this.applyState()
  }

  disconnect() {
    if (!this.mediaQuery) return
    if (this.mediaQuery.removeEventListener) {
      this.mediaQuery.removeEventListener("change", this.handleViewportChange)
    } else {
      this.mediaQuery.removeListener(this.handleViewportChange)
    }
  }

  open() {
    if (this.mediaQuery.matches) return
    this.mobileOpen = true
    this.applyState()
  }

  close() {
    if (this.mediaQuery.matches) return
    this.mobileOpen = false
    this.applyState()
  }

  toggleDesktop() {
    if (!this.mediaQuery.matches) {
      this.mobileOpen = !this.mobileOpen
      this.applyState()
      return
    }

    this.desktopCollapsed = !this.desktopCollapsed
    window.localStorage.setItem("sidebar:desktop-collapsed", this.desktopCollapsed ? "1" : "0")
    this.applyState()
  }

  applyState() {
    const isDesktop = this.mediaQuery.matches

    if (isDesktop) {
      this.mobileOpen = false
      this.panelTarget.classList.remove("-translate-x-full")
      this.backdropTarget.classList.add("hidden")
      this.applyDesktopCollapsedState(this.desktopCollapsed)
      return
    }

    this.applyDesktopCollapsedState(false)
    this.shellTarget.classList.remove("lg:grid-cols-[5rem_minmax(0,1fr)]")
    this.shellTarget.classList.add("lg:grid-cols-[18rem_minmax(0,1fr)]")
    this.panelTarget.classList.toggle("-translate-x-full", !this.mobileOpen)
    this.backdropTarget.classList.toggle("hidden", !this.mobileOpen)
  }

  applyDesktopCollapsedState(collapsed) {
    this.shellTarget.classList.toggle("lg:grid-cols-[18rem_minmax(0,1fr)]", !collapsed)
    this.shellTarget.classList.toggle("lg:grid-cols-[5rem_minmax(0,1fr)]", collapsed)

    this.labelTargets.forEach((label) => label.classList.toggle("hidden", collapsed))
    if (collapsed) {
      this.childrenGroupTargets.forEach((group) => group.classList.add("hidden"))
    }
    this.navLinkTargets.forEach((link) => {
      link.classList.toggle("justify-center", collapsed)
      link.classList.toggle("px-2", collapsed)
      link.classList.toggle("px-3", !collapsed)
    })

    if (this.hasTitleTarget) {
      this.titleTarget.classList.toggle("hidden", collapsed)
    }
    if (this.hasToggleIconTarget) {
      this.toggleIconTarget.setAttribute("data-lucide", collapsed ? "panel-left-open" : "panel-left-close")
      createIcons({ icons, attrs: { class: "h-4 w-4" } })
    }
  }

  toggleChildren(event) {
    event.preventDefault()
    if (this.desktopCollapsed) return

    const button = event.currentTarget
    const groupId = button.dataset.sidebarGroupId
    if (!groupId) return

    const group = document.getElementById(groupId)
    if (!group) return

    const open = group.classList.toggle("hidden") === false
    const icon = button.querySelector("[data-sidebar-target='childrenToggleIcon']")
    if (icon) {
      icon.setAttribute("data-lucide", open ? "chevron-down" : "chevron-right")
      createIcons({ icons, attrs: { class: "h-4 w-4" } })
    }
  }

  handleViewportChange() {
    this.applyState()
  }
}
